/**
 * batch-61 uitrusting-credits-maten (FO-455) — pure samenvatting van de maten
 * van een medewerker voor de leesweergave en de informatieve regel op de
 * bestel-/uitgifteschermen ("Maten: broek 52, shirt L, schoen 44").
 *
 * Regels (CLAUDE.md § Maten):
 *  - de getoonde waarde gaat door `maatNaarOptieWaarde`: een eenduidige
 *    historische schrijfwijze ("C48") wordt de vocabulaire-waarde ("48");
 *  - een waarde die informatie draagt ("44/45", "45 (extra breed)") blijft
 *    letterlijk staan;
 *  - "niet in vocabulaire" hangt aan de canonieke toets (`isMaatLeverbaar`),
 *    nooit aan een `===`-vergelijking.
 */

import { MAAT_TYPES, MAAT_TYPE_LABELS, MAAT_TYPE_KORT, type MaatType } from '@streep/types'
import { isMaatLeverbaar, maatNaarOptieWaarde, type MaatOptie, type MaatProfiel } from './uitrusting-maat'

// Eén bron voor de maattypen en hun labels: `@streep/types` (ook gebruikt door
// de UI); hier alleen hernoemd zodat de bestaande aanroepers blijven werken.
export const MATEN_SAMENVATTING_TYPES = MAAT_TYPES
export type MatenSamenvattingType = MaatType

const LABELS: Record<MatenSamenvattingType, string> = MAAT_TYPE_LABELS
const KORT: Record<MatenSamenvattingType, string> = MAAT_TYPE_KORT

export interface MatenSamenvattingRegel {
  type: MatenSamenvattingType
  label: string
  kort: string
  /** Opgeslagen (rauwe) waarde, of null. */
  raw: string | null
  /** Weergavewaarde: vocabulaire-waarde bij een eenduidige match, anders letterlijk. */
  waarde: string | null
  /** De catalogus voert deze maat niet (canoniek getoetst); leeg = niet gezet. */
  nietInVocabulaire: boolean
}

export interface MatenVocabulaire {
  boven: ReadonlyArray<MaatOptie>
  broek: ReadonlyArray<MaatOptie>
  schoen: ReadonlyArray<MaatOptie>
  handschoen: ReadonlyArray<MaatOptie>
  helm: ReadonlyArray<MaatOptie>
}

const LEEG_VOCABULAIRE: MatenVocabulaire = { boven: [], broek: [], schoen: [], handschoen: [], helm: [] }

function rawVoor(type: MatenSamenvattingType, maten: MaatProfiel): string | null {
  switch (type) {
    case 'boven':
      return maten.kledingmaatBoven
    case 'broek':
      return maten.kledingmaatBroek
    case 'schoen':
      return maten.schoenmaat
    case 'handschoen':
      return maten.handschoenmaat
    case 'helm':
      return maten.helmmaat
  }
}

/**
 * Per maattype de weergave + het vocabulaire-signaal. Volgorde is vast
 * (boven, broek, schoen, handschoen, helm) zodat elk scherm dezelfde volgorde
 * toont.
 */
export function bouwMatenSamenvatting(
  maten: MaatProfiel,
  vocabulaire: MatenVocabulaire | null = null,
): MatenSamenvattingRegel[] {
  const vocab = vocabulaire ?? LEEG_VOCABULAIRE
  return MATEN_SAMENVATTING_TYPES.map((type) => {
    const raw = rawVoor(type, maten)
    const getrimd = (raw ?? '').trim()
    if (getrimd.length === 0) {
      return { type, label: LABELS[type], kort: KORT[type], raw: null, waarde: null, nietInVocabulaire: false }
    }
    const opties = vocab[type]
    const waarde = maatNaarOptieWaarde(getrimd, opties, type)
    const nietInVocabulaire = opties.length > 0 && !isMaatLeverbaar(getrimd, opties, type)
    return { type, label: LABELS[type], kort: KORT[type], raw: getrimd, waarde, nietInVocabulaire }
  })
}

/** True als geen enkele maat is ingevuld (de lege staat, FO-455 §4.2). */
export function matenZijnLeeg(maten: MaatProfiel): boolean {
  return bouwMatenSamenvatting(maten).every((r) => r.waarde === null)
}

/**
 * De informatieve regel: "broek 52, shirt L, schoen 44" (alleen ingevulde
 * maten, in de vaste volgorde). Leeg = "" zodat de caller "niet ingevuld" kan tonen.
 */
export function matenSamenvattingTekst(
  maten: MaatProfiel,
  vocabulaire: MatenVocabulaire | null = null,
): string {
  return bouwMatenSamenvatting(maten, vocabulaire)
    .filter((r): r is MatenSamenvattingRegel & { waarde: string } => r.waarde !== null)
    .map((r) => `${r.kort} ${r.waarde}`)
    .join(', ')
}
